import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
} from "@tanstack/react-query";
import { Loader2, Trash2, Star, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import { useUrlState } from "@/hooks/useUrlState";
import { ProductSearch } from "@/components/products/ProductSearch";
import {
  listFeatured,
  createFeatured,
  deleteFeatured,
} from "@/services/featuredProductsApi";
import { getProduct } from "@/services/productsApi";
import { ApiError } from "@/services/api";
import type { FeaturedPageUrlState } from "@/types/pageState";
import type { Product, FeaturedContext } from "@/types/products";
import { FEATURED_CONTEXTS } from "@/types/products";

const featuredKeys = {
  all: ["featured-products"] as const,
  list: (context: string) => [...featuredKeys.all, "list", context] as const,
};

export default function Featured() {
  const [urlState, setUrlState] = useUrlState<FeaturedPageUrlState>({
    schema: {
      context: "string",
      page: "number",
    },
    defaults: { context: "homepage" },
    replace: true,
  });

  const context: FeaturedContext =
    (urlState.context as FeaturedContext) ?? "homepage";
  const queryClient = useQueryClient();

  // Load featured list
  const { data: featuredList = [], isLoading: loadingFeatured } = useQuery({
    queryKey: featuredKeys.list(context),
    queryFn: () => listFeatured({ context }),
  });

  // Resolve product details for every featured entry in parallel
  const productQueries = useQueries({
    queries: featuredList.map((fp) => ({
      queryKey: ["product", fp.product_id],
      queryFn: () => getProduct(fp.product_id),
      staleTime: 60_000,
    })),
  });

  // Feature a product
  const featureMutation = useMutation({
    mutationFn: (product: Product) =>
      createFeatured({ product_id: product.details.id!, context }),
    onSuccess: (_data, product) => {
      toast.success(`"${product.details.name}" added to ${context}.`);
      queryClient.invalidateQueries({ queryKey: featuredKeys.all });
    },
    onError: (err, product) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.warning(
          `"${product.details.name}" is already featured in ${context}.`,
        );
      } else {
        toast.error(
          err instanceof Error ? err.message : "Failed to feature product.",
        );
      }
    },
  });

  // Remove a featured entry
  const removeMutation = useMutation({
    mutationFn: (id: number) => deleteFeatured(id),
    onSuccess: () => {
      toast.success("Removed from featured.");
      queryClient.invalidateQueries({ queryKey: featuredKeys.all });
    },
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to remove featured product.",
      );
    },
  });

  const handleContextChange = (value: string) => {
    setUrlState((prev) => ({ ...prev, context: value as FeaturedContext }));
  };

  return (
    <>
      <Toaster position="top-right" theme="dark" />
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Featured Products</h2>

        {/* Context selector */}
        <div className="flex items-end gap-4">
          <div className="space-y-2">
            <Label htmlFor="featured-context">Context</Label>
            <Select value={context} onValueChange={handleContextChange}>
              <SelectTrigger id="featured-context" className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FEATURED_CONTEXTS.map((ctx) => (
                  <SelectItem key={ctx} value={ctx}>
                    {ctx.charAt(0).toUpperCase() + ctx.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Search + feature */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              Add a featured product
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Search for a product by ID, variant ID, name, or URL, then click
              "Feature" to add it to the <strong>{context}</strong> context.
            </p>
          </CardHeader>
          <CardContent>
            <ProductSearch
              onSelect={(p) => featureMutation.mutate(p)}
              renderAction={(p) => (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={featureMutation.isPending || p.details.id == null}
                  onClick={() => featureMutation.mutate(p)}
                >
                  {featureMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    "Feature"
                  )}
                </Button>
              )}
            />
          </CardContent>
        </Card>

        {/* Current featured list */}
        <Card>
          <CardHeader>
            <CardTitle>
              Featured in <span className="capitalize">{context}</span>
              {!loadingFeatured && (
                <Badge variant="secondary" className="ml-2 text-sm font-normal">
                  {featuredList.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingFeatured && (
              <div className="flex items-center gap-2 text-muted-foreground py-6">
                <Loader2 className="size-5 animate-spin" />
                Loading…
              </div>
            )}

            {!loadingFeatured && featuredList.length === 0 && (
              <p className="text-sm text-muted-foreground py-4">
                No products featured in this context yet.
              </p>
            )}

            {!loadingFeatured && featuredList.length > 0 && (
              <ul className="divide-y rounded-md border">
                {featuredList.map((fp, idx) => {
                  const q = productQueries[idx];
                  const product = q?.data;
                  const loadingProduct = q?.isLoading ?? false;

                  return (
                    <li
                      key={fp.id}
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      {/* Sort order badge */}
                      <span className="text-xs text-muted-foreground w-6 text-center shrink-0">
                        {fp.sort_order}
                      </span>

                      {/* Product image */}
                      <div className="shrink-0 size-10 rounded border bg-muted flex items-center justify-center overflow-hidden">
                        {loadingProduct ? (
                          <Loader2 className="size-4 animate-spin text-muted-foreground" />
                        ) : product?.details.image_url ? (
                          <img
                            src={product.details.image_url}
                            alt={product.details.name}
                            className="size-full object-cover"
                            onError={(e) => {
                              (
                                e.currentTarget as HTMLImageElement
                              ).style.display = "none";
                            }}
                          />
                        ) : (
                          <ImageOff className="size-5 text-muted-foreground" />
                        )}
                      </div>

                      {/* Product info */}
                      <div className="flex-1 min-w-0">
                        {loadingProduct ? (
                          <span className="text-sm text-muted-foreground">
                            Loading…
                          </span>
                        ) : product ? (
                          <>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm truncate">
                                {product.details.name}
                              </span>
                              {product.details.source && (
                                <Badge variant="secondary" className="text-xs">
                                  {product.details.source_name ??
                                    product.details.source}
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              Product ID {fp.product_id}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            Product ID {fp.product_id}
                          </span>
                        )}
                      </div>

                      {/* Remove button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={removeMutation.isPending}
                        onClick={() => removeMutation.mutate(fp.id)}
                        title="Remove from featured"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
